import { Component } from '@angular/core';

import { AndroidFullScreen } from '@ionic-native/android-full-screen/ngx';
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { AlertController, Platform } from '@ionic/angular';
import { CoreService, IServer } from './api/core/core/core.service';
import { AppConfigService } from './api/core/settings/app-config.service';
import { MainService } from './api/main.service';
import { AnalyticsService } from './api/utils/analytics/analytics.service';
import { DiagnosticsService } from './api/utils/diagnostics/diagnostics.service';

import each from 'lodash/each';

import { datadogRum } from '@datadog/browser-rum';
import { debounceTime } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  private _switcherCount: number = 0;
  private _switchTimeout;

  public buildDate: string;
  public resolution: string;

  // flagger for displaying connection problem
  public connectionMinify: boolean = false;

  // DEV MODE BOX
  public dev_box_minimized: boolean = false;

  constructor(
    public platform: Platform,
    private statusBar: StatusBar,
    public mainService: MainService,
    public alertCtrl: AlertController,
    public core: CoreService,
    public appConfig: AppConfigService,
    public analytics: AnalyticsService,
    public fullscreen: AndroidFullScreen,
    public screenOrientation: ScreenOrientation,
    public diagnostics: DiagnosticsService,
  ) {
    this.initializeApp();
  }

  initializeApp() {
    const compileDate: string = environment.compile_date;
    if (compileDate && compileDate.length > 0 && !compileDate.includes('COMPILE_DATE')) {
      this.buildDate = compileDate;
      this.core.appDeviceInfo.device.version = this.buildDate;
    }

    this.platform.ready().then(async res => {
      this.statusBar.hide();

      if (this.platform.is('cordova')) {
        // screen orientation - lock to landscape
        await this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.LANDSCAPE);

        if (this.platform.is('android')) {
          this.fullscreen.isImmersiveModeSupported().then(supported => {
            this.fullscreen.immersiveMode();
          });
        }

        if (!this.platform.is('mobileweb')) {
          this.platform.pause.subscribe(async () => {
            console.log('============PAUSE============');
            this.mainService.enableBackgroundMode();
          });

          this.platform.resume.pipe(debounceTime(1000)).subscribe(async () => {
            console.log('============RESUME============');
          });
        }
      }

      // start the app
      this.mainService.start();

      // load global settings
      this.appConfig.preloadConfigJSON();

      // hubspot stuff
      this.mainService.analytics.hubspotLoad();

      // add datadog logging
      this.initDatadog();

      // listen to resolution change
      this.checkResolution();
    });
  }

  checkResolution(): void {
    this.resolution = `${this.platform.width()}.${this.platform.height()}`;
    if (this.platform.resize) {
      this.platform.resize.pipe(debounceTime(1000)).subscribe(() => {
        this.resolution = `${this.platform.width()}.${this.platform.height()}`;
      });
    }
  }

  initDatadog(): void {
    if (environment.unit_test) return;

    datadogRum.init({
      applicationId: '1d03bf57-cb1c-4fca-98ee-4bd2b5454618',
      clientToken: 'pub612ada8d31c2e62ac741089181a424bc',
      site: 'datadoghq.com',
      service: 'hungryhungry-order-manager',
      resourceSampleRate: 100,
      sampleRate: 100,
      trackInteractions: false,
      trackSessionAcrossSubdomains: true,
    });
  }

  // HUBSPOT STUFF
  menuWillClose(): void {
    this.analytics.hideHubspot();
  }
  menuDidOpen(): void {
    // this.analytics.showHubspot();
  }

  // minify connection messages
  minify(): void {
    this.connectionMinify = true;
    //enlarge again after 5 mins
    setTimeout(() => {
      this.connectionMinify = false;
    }, 300000);
  }

  async setServer() {
    ++this._switcherCount;

    clearTimeout(this._switchTimeout);

    if (this._switcherCount < 10) {
      this._switchTimeout = setTimeout(() => {
        this._switcherCount = 0;
      }, 2000);
      return;
    }

    this._switcherCount = 0;

    let alertOption: any = {
      header: 'Server',
      inputs: [],
      buttons: [
        {
          text: 'Cancel',
          cssClass: 'secondary',
          handler: data => {
            // do nothing
          },
        },
        {
          text: 'Ok',
          handler: data => {
            if (data) {
              this.core.setServerByEnv(data);
            }
          },
        },
      ],
    };

    each(this.core.Available_servers, (server: IServer) => {
      alertOption.inputs.push({
        name: server.env,
        type: 'radio',
        label: server.label,
        value: server.env,
        checked: server.env == this.core.Active_server.env,
      });
    });

    let alert = await this.alertCtrl.create(alertOption);
    alert.present();
  }
}
